import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, ExternalLink, Copy, Check, AlertCircle, Plus, Trash2, Pencil, Download, ChevronDown, ChevronUp } from 'lucide-react';
import { api, TenantStatus, ScheduledReport } from '../services/api';
import { Select } from '../components/Select';
import SEOHead from '../components/SEOHead';
import { PAGE_SEO } from '../seo';

type Step = 'organization' | 'slack' | 'ace' | 'crm' | 'channels' | 'complete';

const STEPS: { id: Step; label: string }[] = [
  { id: 'organization', label: 'Organization' },
  { id: 'slack', label: 'Connect Workspace' },
  { id: 'ace', label: 'AWS Access' },
  { id: 'crm', label: 'CRM Integration' },
  { id: 'channels', label: 'Configure Channels' },
  { id: 'complete', label: 'Complete' },
];

export default function SetupWizard() {
  const { tenantId, step } = useParams<{ tenantId: string; step?: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [tenant, setTenant] = useState<TenantStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<Step>('organization');
  const [oauthError, setOauthError] = useState<string | null>(null);

  // Read OAuth error and token from URL params (set by OAuth callback redirects)
  useEffect(() => {
    const token = searchParams.get('token');
    const oauthErr = searchParams.get('oauth_error');
    const message = searchParams.get('message');

    // Store session token if provided (from OAuth callback redirect)
    if (token) {
      sessionStorage.setItem('partnerbuddy_session_token', token);
    }

    // Show OAuth error message if present
    if (oauthErr) {
      const friendlyMessages: Record<string, string> = {
        salesforce_oauth_failed: message || 'Salesforce denied the authorization request.',
        hubspot_oauth_failed: message || 'HubSpot denied the authorization request.',
        token_exchange_failed: message || 'Failed to complete authorization. Please check your app credentials and try again.',
        invalid_oauth_state: 'Authorization session expired. Please try again.',
        missing_credentials: message || 'App credentials not found. Please re-enter them below.',
        callback_failed: message || 'Authorization failed unexpectedly. Please try again.',
        missing_params: message || 'Missing authorization code. Please try again.',
      };
      setOauthError(friendlyMessages[oauthErr] || message || 'Authorization failed. Please try again.');

      // Clean the URL params so the error doesn't persist on refresh
      searchParams.delete('oauth_error');
      searchParams.delete('message');
      searchParams.delete('token');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  useEffect(() => {
    if (!tenantId) {
      navigate('/');
      return;
    }
    loadTenant();
  }, [tenantId]);

  useEffect(() => {
    if (step && ['organization', 'slack', 'ace', 'crm', 'hubspot', 'channels', 'complete'].includes(step)) {
      // Map legacy 'hubspot' step to 'crm'
      setCurrentStep(step === 'hubspot' ? 'crm' : step as Step);
    }
  }, [step]);

  useEffect(() => {
    // Auto-advance based on tenant status
    if (tenant) {
      const platformConfigured = tenant.slack_configured || tenant.teams_configured;

      if (tenant.status === 'active') {
        setCurrentStep('complete');
      } else if (tenant.status === 'pending_channel_setup') {
        // CRM step comes before channels - check if CRM still needs configuration.
        // An explicit skip (crm_skipped) counts as done, else skip loops back here.
        if (!tenant.crm_type && !tenant.hubspot_configured && !tenant.salesforce_configured && !tenant.crm_skipped) {
          setCurrentStep('crm');
        } else {
          setCurrentStep('channels');
        }
      } else if (!tenant.organization_configured) {
        // Organization step is first - if not configured, start here
        setCurrentStep('organization');
      } else if (!platformConfigured) {
        // Platform step (Slack or Teams) - renamed from 'slack' but kept the id for compatibility
        setCurrentStep('slack');
      } else if (platformConfigured && !tenant.ace_role_configured) {
        setCurrentStep('ace');
      } else if (tenant.ace_role_configured && !tenant.crm_type && !tenant.hubspot_configured && !tenant.salesforce_configured && !tenant.crm_skipped) {
        setCurrentStep('crm');
      } else if (tenant.crm_type || tenant.hubspot_configured || tenant.salesforce_configured || tenant.crm_skipped) {
        setCurrentStep('channels');
      }
    }
  }, [tenant]);

  const loadTenant = async () => {
    try {
      setLoading(true);
      const data = await api.getTenantStatus(tenantId!);
      setTenant(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tenant');
    } finally {
      setLoading(false);
    }
  };

  const getStepStatus = (stepId: Step): 'complete' | 'current' | 'upcoming' => {
    if (!tenant) return 'upcoming';
    
    const stepIndex = STEPS.findIndex(s => s.id === stepId);
    const currentIndex = STEPS.findIndex(s => s.id === currentStep);
    
    // When on the "complete" step, show all steps as complete (including "complete" itself)
    if (currentStep === 'complete') return 'complete';
    
    if (stepIndex < currentIndex) return 'complete';
    if (stepIndex === currentIndex) return 'current';
    return 'upcoming';
  };

  if (loading) {
    return (
      <div className="min-h-screen py-12 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Skeleton header */}
          <div className="text-center mb-10">
            <div className="skeleton h-14 w-48 mx-auto mb-4" />
            <div className="skeleton h-5 w-80 mx-auto" />
          </div>
          {/* Skeleton progress bar */}
          <div className="flex items-center gap-4 mb-10">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-2">
                <div className="skeleton w-10 h-10 rounded-full" />
                <div className="skeleton h-3 w-16" />
              </div>
            ))}
          </div>
          {/* Skeleton content card */}
          <div className="bg-white rounded-2xl shadow-rhythmic-xl p-8">
            <div className="skeleton h-16 w-16 rounded-full mx-auto mb-6" />
            <div className="skeleton h-6 w-64 mx-auto mb-3" />
            <div className="skeleton h-4 w-96 mx-auto mb-8" />
            <div className="space-y-4 max-w-md mx-auto">
              <div className="skeleton h-12 w-full" />
              <div className="skeleton h-12 w-full" />
              <div className="skeleton h-12 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-rhythmic-xl p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-display font-bold text-pb-gray-800 mb-2">
            Setup Error
          </h1>
          <p className="text-pb-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-pb-orange text-white rounded-lg font-semibold hover:bg-pb-orange-dark hover:shadow-lg transition-all"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4">
      <SEOHead page={PAGE_SEO.setup} />
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10 animate-fadeIn">
          <img
            src="/partnerbuddy-logo.png"
            alt="PartnerBuddy"
            className="h-14 mx-auto mb-4"
          />
          <p className="text-pb-gray-500">
            Complete the steps below to activate your PartnerBuddy integration
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-10">
          <div className="flex items-start">
            {STEPS.map((s, idx) => {
              const status = getStepStatus(s.id);
              return (
                <div key={s.id} className="flex items-start flex-1 last:flex-none">
                  {/* Icon + Label column */}
                  <div className="flex flex-col items-center" style={{ width: '80px' }}>
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                        status === 'complete'
                          ? 'bg-pb-blue text-white'
                          : status === 'current'
                          ? 'bg-pb-orange text-white'
                          : 'bg-pb-gray-200 text-pb-gray-500'
                      }`}
                    >
                      {status === 'complete' ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <span className="font-semibold">{idx + 1}</span>
                      )}
                    </div>
                    <span
                      className={`mt-2 text-xs font-medium text-center ${
                        status === 'current'
                          ? 'text-pb-orange'
                          : status === 'complete'
                          ? 'text-pb-blue'
                          : 'text-pb-gray-500'
                      }`}
                    >
                      {s.label}
                    </span>
                  </div>
                  {/* Connector line */}
                  {idx < STEPS.length - 1 && (
                    <div
                      className={`h-1 flex-1 rounded mt-5 ${
                        getStepStatus(STEPS[idx + 1].id) !== 'upcoming'
                          ? 'bg-pb-blue'
                          : 'bg-pb-gray-200'
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-2xl shadow-rhythmic-xl overflow-hidden animate-fadeInUp">
          {currentStep === 'organization' && tenant && (
            <OrganizationStep tenant={tenant} onComplete={loadTenant} />
          )}
          {currentStep === 'slack' && tenant && (
            <SlackStep tenant={tenant} onComplete={loadTenant} />
          )}
          {currentStep === 'ace' && tenant && (
            <AceStep tenant={tenant} onComplete={loadTenant} />
          )}
          {currentStep === 'crm' && tenant && (
            <CRMStep tenant={tenant} onComplete={loadTenant} oauthError={oauthError} onClearError={() => setOauthError(null)} />
          )}
          {currentStep === 'channels' && tenant && (
            <ChannelsStep tenant={tenant} onComplete={() => setCurrentStep('complete')} />
          )}
          {currentStep === 'complete' && tenant && (
            <CompleteStep tenant={tenant} />
          )}
        </div>
      </div>
    </div>
  );
}

// ----- Step Components -----

// Common timezones for the dropdown
const COMMON_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
];

function OrganizationStep({ tenant, onComplete }: { tenant: TenantStatus; onComplete: () => void }) {
  const [organizationName, setOrganizationName] = useState(tenant.organization_name || '');
  const [adminEmail, setAdminEmail] = useState(tenant.admin_email || '');
  const [timezone, setTimezone] = useState(tenant.timezone || 'America/New_York');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If already configured, show success state
  if (tenant.organization_configured) {
    return (
      <div className="p-8 text-center">
        <CheckCircle className="w-16 h-16 text-pb-blue mx-auto mb-4" />
        <h2 className="text-xl font-display font-bold text-pb-gray-800 mb-2">
          Organization Configured
        </h2>
        <p className="text-pb-gray-600 mb-4">
          <strong>{tenant.organization_name}</strong>
        </p>
        <p className="text-pb-gray-500 text-sm mb-6">
          Admin: {tenant.admin_email}
        </p>
        <button
          onClick={onComplete}
          className="px-6 py-3 bg-pb-orange text-white rounded-lg font-semibold hover:bg-pb-orange-dark hover:shadow-lg transition-all"
        >
          Continue to Next Step
        </button>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      // Basic validation
      if (!organizationName.trim()) {
        throw new Error('Organization name is required');
      }
      if (!adminEmail.trim()) {
        throw new Error('Admin email is required');
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adminEmail)) {
        throw new Error('Please enter a valid email address');
      }

      await api.updateOrganization(tenant.tenant_id, {
        organization_name: organizationName.trim(),
        admin_email: adminEmail.trim(),
        timezone,
      });

      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save organization details');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-pb-orange/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-pb-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        </div>
        <h2 className="text-xl font-display font-bold text-pb-gray-800 mb-2">
          Organization Details
        </h2>
        <p className="text-pb-gray-600">
          Tell us about your organization to personalize your PartnerBuddy experience.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-md mx-auto space-y-6">
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          </div>
        )}

        <div>
          <label htmlFor="organizationName" className="block text-sm font-medium text-pb-gray-700 mb-2">
            Organization Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="organizationName"
            value={organizationName}
            onChange={(e) => setOrganizationName(e.target.value)}
            placeholder="Acme Corporation"
            className="w-full px-4 py-3 border border-pb-gray-300 rounded-lg focus:ring-2 focus:ring-pb-orange focus:border-transparent transition-all"
            required
          />
        </div>

        <div>
          <label htmlFor="adminEmail" className="block text-sm font-medium text-pb-gray-700 mb-2">
            Admin Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="adminEmail"
            value={adminEmail}
            onChange={(e) => setAdminEmail(e.target.value)}
            placeholder="admin@acme.com"
            className="w-full px-4 py-3 border border-pb-gray-300 rounded-lg focus:ring-2 focus:ring-pb-orange focus:border-transparent transition-all"
            required
          />
          <p className="mt-1 text-sm text-pb-gray-500">
            Used for important notifications about your subscription.
          </p>
        </div>

        <div>
          <label htmlFor="timezone" className="block text-sm font-medium text-pb-gray-700 mb-2">
            Timezone
          </label>
          <select
            id="timezone"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className="w-full px-4 py-3 border border-pb-gray-300 rounded-lg focus:ring-2 focus:ring-pb-orange focus:border-transparent transition-all"
          >
            {COMMON_TIMEZONES.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-sm text-pb-gray-500">
            Used for scheduling automated reports.
          </p>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="w-full px-6 py-3 bg-pb-orange text-white rounded-lg font-semibold hover:bg-pb-orange-dark hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {saving ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Saving...
            </>
          ) : (
            'Continue'
          )}
        </button>
      </form>
    </div>
  );
}

function SlackStep({ tenant, onComplete }: { tenant: TenantStatus; onComplete: () => void }) {
  const [selectedPlatform, setSelectedPlatform] = useState<'slack' | 'teams' | null>(
    tenant.slack_configured ? 'slack' : tenant.teams_configured ? 'teams' : null
  );

  // If already connected, show success state
  if (tenant.slack_configured) {
    return (
      <div className="p-8 text-center">
        <CheckCircle className="w-16 h-16 text-pb-blue mx-auto mb-4" />
        <h2 className="text-xl font-display font-bold text-pb-gray-800 mb-2">
          Slack Connected
        </h2>
        <p className="text-pb-gray-600 mb-4">
          Connected to workspace: <strong>{tenant.workspace_name}</strong>
        </p>
        <button
          onClick={onComplete}
          className="px-6 py-3 bg-pb-orange text-white rounded-lg font-semibold hover:bg-pb-orange-dark hover:shadow-lg transition-all"
        >
          Continue to AWS Setup
        </button>
      </div>
    );
  }

  if (tenant.teams_configured) {
    return (
      <div className="p-8 text-center">
        <CheckCircle className="w-16 h-16 text-pb-blue mx-auto mb-4" />
        <h2 className="text-xl font-display font-bold text-pb-gray-800 mb-2">
          Microsoft Teams Connected
        </h2>
        <p className="text-pb-gray-600 mb-4">
          Connected to: <strong>{tenant.teams_tenant_name || 'Your Organization'}</strong>
        </p>
        <button
          onClick={onComplete}
          className="px-6 py-3 bg-pb-orange text-white rounded-lg font-semibold hover:bg-pb-orange-dark hover:shadow-lg transition-all"
        >
          Continue to AWS Setup
        </button>
      </div>
    );
  }

  // Platform selection screen
  if (!selectedPlatform) {
    return (
      <div className="p-8">
        <div className="text-center mb-8">
          <h2 className="text-xl font-display font-bold text-pb-gray-800 mb-2">
            Choose Your Platform
          </h2>
          <p className="text-pb-gray-600">
            Select where you want to use PartnerBuddy
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4 max-w-lg mx-auto">
          {/* Slack Option */}
          <button
            onClick={() => setSelectedPlatform('slack')}
            className="p-6 border-2 border-pb-gray-200 rounded-xl hover:border-[#4A154B] hover:bg-purple-50 transition-all group"
          >
            <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:bg-purple-200 transition-colors">
              <svg className="w-8 h-8 text-[#4A154B]" viewBox="0 0 24 24" fill="currentColor">
                <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
              </svg>
            </div>
            <h3 className="font-semibold text-pb-gray-800">Slack</h3>
            <p className="text-sm text-pb-gray-500 mt-1">Connect via OAuth</p>
          </button>

          {/* Teams Option */}
          <button
            onClick={() => setSelectedPlatform('teams')}
            className="p-6 border-2 border-pb-gray-200 rounded-xl hover:border-[#5558AF] hover:bg-indigo-50 transition-all group"
          >
            <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:bg-indigo-200 transition-colors">
              <svg className="w-8 h-8 text-[#5558AF]" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.625 8.5h-6.187a.938.938 0 0 0-.938.938v6.187c0 .518.42.938.938.938h6.187c.518 0 .938-.42.938-.938V9.438a.938.938 0 0 0-.938-.938zM17 6.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM11.5 5a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0zM9 8c-3.309 0-6 2.691-6 6v3.5a.5.5 0 0 0 .5.5h11a.5.5 0 0 0 .5-.5V14c0-3.309-2.691-6-6-6z"/>
              </svg>
            </div>
            <h3 className="font-semibold text-pb-gray-800">Microsoft Teams</h3>
            <p className="text-sm text-pb-gray-500 mt-1">Install Teams app</p>
          </button>
        </div>
      </div>
    );
  }

  // Teams setup screen
  if (selectedPlatform === 'teams') {
    return <TeamsSetupScreen tenant={tenant} onComplete={onComplete} onBack={() => setSelectedPlatform(null)} />;
  }

  // Slack setup screen
  return (
    <div className="p-8">
      <button
        onClick={() => setSelectedPlatform(null)}
        className="text-pb-gray-500 hover:text-pb-gray-700 mb-4 flex items-center text-sm"
      >
        ← Back to platform selection
      </button>
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-[#4A154B]" viewBox="0 0 24 24" fill="currentColor">
            <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
          </svg>
        </div>
        <h2 className="text-xl font-display font-bold text-pb-gray-800 mb-2">
          Connect Your Slack Workspace
        </h2>
        <p className="text-pb-gray-600">
          Allow PartnerBuddy to respond to messages in your Slack workspace
        </p>
      </div>

      <div className="bg-pb-gray-50 rounded-xl p-6 mb-6">
        <h3 className="font-semibold text-pb-gray-800 mb-3">PartnerBuddy will be able to:</h3>
        <ul className="space-y-2 text-sm text-pb-gray-600">
          <li className="flex items-center">
            <CheckCircle className="w-4 h-4 text-pb-blue mr-2 flex-shrink-0" />
            Read messages where @PartnerBuddy is mentioned
          </li>
          <li className="flex items-center">
            <CheckCircle className="w-4 h-4 text-pb-blue mr-2 flex-shrink-0" />
            Send messages and respond in threads
          </li>
          <li className="flex items-center">
            <CheckCircle className="w-4 h-4 text-pb-blue mr-2 flex-shrink-0" />
            Add reactions to acknowledge messages
          </li>
        </ul>
      </div>

      <a
        href={api.getSlackOAuthUrl(tenant.tenant_id)}
        className="w-full flex items-center justify-center px-6 py-4 bg-[#4A154B] text-white rounded-xl font-semibold hover:bg-[#3e1240] transition-colors"
      >
        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
          <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
        </svg>
        Add to Slack
      </a>
    </div>
  );
}

// Teams setup screen with sideload support (temporary - remove after marketplace approval)
const TEAMS_SIDELOAD_ZIP_URL = import.meta.env.VITE_TEAMS_SIDELOAD_URL || 'https://app.partnerbuddy.ai/assets/partnerbuddy-teams.zip';

function TeamsSetupScreen({ tenant, onComplete, onBack }: { tenant: TenantStatus; onComplete: () => void; onBack: () => void }) {
  const [showSideloadInstructions, setShowSideloadInstructions] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [teamsTenantId, setTeamsTenantId] = useState('');

  const handleManualConfirm = async () => {
    if (!teamsTenantId.trim()) {
      setError('Please enter your Azure AD Tenant ID');
      return;
    }
    setConfirming(true);
    setError(null);
    try {
      await api.confirmTeamsManual(tenant.tenant_id, teamsTenantId.trim());
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to confirm Teams installation');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="p-8">
      <button
        onClick={onBack}
        className="text-pb-gray-500 hover:text-pb-gray-700 mb-4 flex items-center text-sm"
      >
        ← Back to platform selection
      </button>
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-[#5558AF]" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20.625 8.5h-6.187a.938.938 0 0 0-.938.938v6.187c0 .518.42.938.938.938h6.187c.518 0 .938-.42.938-.938V9.438a.938.938 0 0 0-.938-.938zM17 6.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM11.5 5a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0zM9 8c-3.309 0-6 2.691-6 6v3.5a.5.5 0 0 0 .5.5h11a.5.5 0 0 0 .5-.5V14c0-3.309-2.691-6-6-6z"/>
          </svg>
        </div>
        <h2 className="text-xl font-display font-bold text-pb-gray-800 mb-2">
          Connect Microsoft Teams
        </h2>
        <p className="text-pb-gray-600">
          Install the PartnerBuddy app in your Microsoft Teams workspace
        </p>
      </div>

      <div className="bg-pb-gray-50 rounded-xl p-6 mb-6">
        <h3 className="font-semibold text-pb-gray-800 mb-3">PartnerBuddy will be able to:</h3>
        <ul className="space-y-2 text-sm text-pb-gray-600">
          <li className="flex items-center">
            <CheckCircle className="w-4 h-4 text-pb-blue mr-2 flex-shrink-0" />
            Respond to messages in Teams channels and chats
          </li>
          <li className="flex items-center">
            <CheckCircle className="w-4 h-4 text-pb-blue mr-2 flex-shrink-0" />
            Send proactive notifications about opportunities
          </li>
          <li className="flex items-center">
            <CheckCircle className="w-4 h-4 text-pb-blue mr-2 flex-shrink-0" />
            Work with your ACE opportunities via natural language
          </li>
        </ul>
      </div>

      {/* Sideload Installation Section */}
      <div className="border border-pb-gray-200 rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-pb-gray-800">Manual Installation (Sideload)</h3>
          <span className="text-xs bg-pb-orange/10 text-pb-orange px-2 py-1 rounded-full">Pre-marketplace</span>
        </div>

        <a
          href={TEAMS_SIDELOAD_ZIP_URL}
          download
          className="w-full flex items-center justify-center px-6 py-3 bg-pb-gray-100 text-pb-gray-800 rounded-lg font-medium hover:bg-pb-gray-200 transition-colors mb-4"
        >
          <Download className="w-5 h-5 mr-2" />
          Download App Package (.zip)
        </a>

        <button
          onClick={() => setShowSideloadInstructions(!showSideloadInstructions)}
          className="w-full flex items-center justify-between text-sm text-pb-gray-600 hover:text-pb-gray-800"
        >
          <span>Installation Instructions</span>
          {showSideloadInstructions ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {showSideloadInstructions && (
          <ol className="mt-4 space-y-3 text-sm text-pb-gray-600 list-decimal list-inside">
            <li>Download the Teams app package above</li>
            <li>Go to <a href="https://admin.teams.microsoft.com/policies/manage-apps" target="_blank" rel="noopener noreferrer" className="text-pb-blue hover:underline">Teams Admin Center → Manage apps</a></li>
            <li>Click "Upload new app" and select the ZIP file</li>
            <li>Review permissions and click "Publish"</li>
            <li>Install the app in your team or for users</li>
            <li>Send "setup verify" to the bot, or click the button below</li>
          </ol>
        )}

        <div className="mt-4">
          <label htmlFor="teamsTenantId" className="block text-sm font-medium text-pb-gray-700 mb-1">
            Azure AD Tenant ID
          </label>
          <input
            id="teamsTenantId"
            type="text"
            value={teamsTenantId}
            onChange={(e) => setTeamsTenantId(e.target.value)}
            placeholder="e.g. 12345678-abcd-1234-abcd-123456789abc"
            className="w-full px-3 py-2 border border-pb-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pb-blue focus:border-pb-blue"
          />
          <p className="mt-1 text-xs text-pb-gray-500">
            Find this in <a href="https://entra.microsoft.com/#view/Microsoft_AAD_IAM/TenantOverview.ReactView" target="_blank" rel="noopener noreferrer" className="text-pb-blue hover:underline">Microsoft Entra admin center</a> → Overview → Tenant ID
          </p>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center">
            <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
            {error}
          </div>
        )}

        <button
          onClick={handleManualConfirm}
          disabled={confirming || !teamsTenantId.trim()}
          className="w-full mt-4 flex items-center justify-center px-6 py-3 bg-[#5558AF] text-white rounded-lg font-semibold hover:bg-[#464996] transition-colors disabled:opacity-50"
        >
          {confirming ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Confirming...
            </>
          ) : (
            <>
              <CheckCircle className="w-5 h-5 mr-2" />
              I've Installed the App Manually
            </>
          )}
        </button>
      </div>

      {/* Divider */}
      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-pb-gray-200"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-white text-pb-gray-500">OR</span>
        </div>
      </div>

      {/* Marketplace Link (for when available) */}
      <div className="text-center">
        <p className="text-sm text-pb-gray-500 mb-3">If you have marketplace access:</p>
        <a
          href={`https://teams.microsoft.com/l/app/${encodeURIComponent(tenant.tenant_id)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center px-6 py-3 border-2 border-[#5558AF] text-[#5558AF] rounded-lg font-semibold hover:bg-indigo-50 transition-colors"
        >
          <ExternalLink className="w-5 h-5 mr-2" />
          Add to Microsoft Teams
        </a>
        <p className="text-xs text-pb-gray-500 mt-2">
          Requires admin consent for your Microsoft 365 organization
        </p>
      </div>
    </div>
  );
}

function AceStep({ tenant, onComplete }: { tenant: TenantStatus; onComplete: () => void }) {
  const [roleArn, setRoleArn] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const copyExternalId = () => {
    navigator.clipboard.writeText(tenant.ace_external_id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleVerify = async () => {
    if (!roleArn.trim()) return;

    setVerifying(true);
    setVerifyError(null);

    try {
      const result = await api.verifyAceRole(tenant.tenant_id, roleArn);
      
      if (result.status === 'success') {
        onComplete();
      } else if (result.status === 'partial') {
        setVerifyError(result.ace_error || 'ACE API access failed');
      } else {
        setVerifyError(result.message);
      }
    } catch (err) {
      setVerifyError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  if (tenant.ace_role_configured) {
    return (
      <div className="p-8 text-center">
        <CheckCircle className="w-16 h-16 text-pb-blue mx-auto mb-4" />
        <h2 className="text-xl font-display font-bold text-pb-gray-800 mb-2">
          AWS Access Configured
        </h2>
        <p className="text-pb-gray-600 mb-4">
          PartnerBuddy can access your AWS Partner Central data
        </p>
        <button
          onClick={onComplete}
          className="px-6 py-3 bg-pb-orange text-white rounded-lg font-semibold hover:bg-pb-orange-dark hover:shadow-lg transition-all"
        >
          Continue to Channels
        </button>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-orange-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2L2 7l10 5 10-5-10-5z" />
            <path d="M2 17l10 5 10-5" />
            <path d="M2 12l10 5 10-5" />
          </svg>
        </div>
        <h2 className="text-xl font-display font-bold text-pb-gray-800 mb-2">
          Grant AWS Partner Central Access
        </h2>
        <p className="text-pb-gray-600">
          Deploy a CloudFormation stack to allow PartnerBuddy to read your ACE data
        </p>
      </div>

      {/* Step 1: External ID */}
      <div className="mb-6">
        <h3 className="font-semibold text-pb-gray-800 mb-3 flex items-center">
          <span className="w-6 h-6 rounded-full bg-pb-orange text-white text-sm flex items-center justify-center mr-2">1</span>
          Copy your External ID
        </h3>
        <div className="flex items-center bg-pb-gray-50 rounded-lg p-3">
          <code className="flex-1 font-mono text-sm text-pb-gray-800">
            {tenant.ace_external_id}
          </code>
          <button
            onClick={copyExternalId}
            className="ml-2 p-2 text-pb-gray-500 hover:text-pb-orange transition-colors"
          >
            {copied ? <Check className="w-5 h-5 text-pb-blue" /> : <Copy className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Step 2: Deploy CloudFormation */}
      <div className="mb-6">
        <h3 className="font-semibold text-pb-gray-800 mb-3 flex items-center">
          <span className="w-6 h-6 rounded-full bg-pb-orange text-white text-sm flex items-center justify-center mr-2">2</span>
          Deploy the CloudFormation Stack
        </h3>
        <p className="text-sm text-pb-gray-600 mb-3">
          Click the button below to open AWS CloudFormation with our pre-configured template.
          The External ID will be pre-filled.
        </p>
        <a
          href={api.getCloudFormationUrl(tenant.ace_external_id)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center w-full px-6 py-4 bg-[#232f3e] text-white rounded-xl font-semibold hover:bg-[#1a2433] transition-colors"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
          </svg>
          Launch CloudFormation Stack
          <ExternalLink className="w-4 h-4 ml-2" />
        </a>
      </div>

      {/* Step 3: Enter Role ARN */}
      <div className="mb-6">
        <h3 className="font-semibold text-pb-gray-800 mb-3 flex items-center">
          <span className="w-6 h-6 rounded-full bg-pb-orange text-white text-sm flex items-center justify-center mr-2">3</span>
          Enter the Role ARN from CloudFormation Outputs
        </h3>
        <input
          type="text"
          value={roleArn}
          onChange={(e) => setRoleArn(e.target.value)}
          placeholder="arn:aws:iam::123456789012:role/partnerbuddy-cross-account-role"
          className="w-full px-4 py-3 border-2 border-pb-gray-200 rounded-lg font-mono text-sm focus:border-pb-orange focus:ring-2 focus:ring-pb-orange/20 outline-none transition-all"
        />
      </div>

      {verifyError && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-lg">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
            <p className="text-sm">{verifyError}</p>
          </div>
        </div>
      )}

      <button
        onClick={handleVerify}
        disabled={!roleArn.trim() || verifying}
        className="w-full px-6 py-4 bg-pb-orange text-white rounded-xl font-semibold hover:bg-pb-orange-dark hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {verifying ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Verifying...
          </>
        ) : (
          'Verify & Continue'
        )}
      </button>
    </div>
  );
}

type CRMChoice = 'salesforce' | 'hubspot' | 'skip' | null;

function CRMStep({ tenant, onComplete, oauthError, onClearError }: { tenant: TenantStatus; onComplete: () => void; oauthError?: string | null; onClearError?: () => void }) {
  const [crmChoice, setCrmChoice] = useState<CRMChoice>(null);

  // Check if CRM is already configured
  const isConfigured = tenant.crm_type || tenant.hubspot_configured || tenant.salesforce_configured;

  if (isConfigured) {
    const configuredCRM = tenant.crm_type || (tenant.hubspot_configured ? 'hubspot' : 'salesforce');
    return (
      <div className="p-8 text-center">
        <CheckCircle className="w-16 h-16 text-pb-blue mx-auto mb-4" />
        <h2 className="text-xl font-display font-bold text-pb-gray-800 mb-2">
          {configuredCRM === 'hubspot' ? 'HubSpot' : 'Salesforce'} Connected
        </h2>
        <p className="text-pb-gray-600 mb-4">
          PartnerBuddy can sync your {configuredCRM === 'hubspot' ? 'HubSpot deals' : 'Salesforce opportunities'} with ACE
        </p>
        <button
          onClick={onComplete}
          className="px-6 py-3 bg-pb-orange text-white rounded-lg font-semibold hover:bg-pb-orange-dark hover:shadow-lg transition-all"
        >
          Continue to Channels
        </button>
      </div>
    );
  }

  // Show CRM choice selection
  if (crmChoice === null) {
    return (
      <div className="p-8">
        {oauthError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-800">CRM Authorization Failed</p>
              <p className="text-sm text-red-700 mt-1">{oauthError}</p>
              <p className="text-sm text-red-600 mt-2">Please check your app credentials and try again.</p>
            </div>
            <button onClick={onClearError} className="text-red-400 hover:text-red-600 p-1">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-pb-blue/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-pb-blue" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <h2 className="text-xl font-display font-bold text-pb-gray-800 mb-2">
            Connect Your CRM (Optional)
          </h2>
          <p className="text-pb-gray-600">
            Sync opportunities from your CRM to AWS Partner Central
          </p>
        </div>

        <div className="space-y-4 mb-6">
          {/* Salesforce Option */}
          <button
            onClick={() => setCrmChoice('salesforce')}
            className="w-full p-6 border-2 border-pb-gray-200 rounded-xl hover:border-blue-400 hover:bg-blue-50/50 transition-all text-left group"
          >
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mr-4 group-hover:bg-blue-200 transition-colors">
                <svg className="w-6 h-6 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12.584 4.266c-.668-.68-1.59-1.077-2.584-1.077-1.994 0-3.61 1.616-3.61 3.61 0 .468.095.91.254 1.32-1.494.168-2.644 1.424-2.644 2.96 0 1.645 1.333 2.978 2.977 2.978h.018c-.013.105-.02.212-.02.32 0 1.645 1.332 2.977 2.976 2.977.468 0 .91-.108 1.305-.298.395.76 1.18 1.284 2.089 1.284.878 0 1.642-.484 2.044-1.197.328.122.684.19 1.054.19 1.645 0 2.977-1.332 2.977-2.977 0-.467-.108-.909-.297-1.304.76-.395 1.284-1.18 1.284-2.089 0-1.3-1.053-2.354-2.354-2.354-.062 0-.123.003-.183.008.005-.06.008-.12.008-.182 0-1.994-1.616-3.61-3.61-3.61-.994 0-1.895.402-2.549 1.052-.016.016-.03.033-.045.05-.01-.013-.025-.024-.036-.037z"/>
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-pb-gray-800 group-hover:text-blue-700">Salesforce</h3>
                <p className="text-sm text-pb-gray-600">Connect via OAuth to sync Opportunities</p>
              </div>
              <ExternalLink className="w-5 h-5 text-pb-gray-400 group-hover:text-blue-600" />
            </div>
          </button>

          {/* HubSpot Option */}
          <button
            onClick={() => setCrmChoice('hubspot')}
            className="w-full p-6 border-2 border-pb-gray-200 rounded-xl hover:border-orange-400 hover:bg-orange-50/50 transition-all text-left group"
          >
            <div className="flex items-center">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mr-4 group-hover:bg-orange-200 transition-colors">
                <svg className="w-6 h-6 text-orange-500" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.164 7.93V5.084a2.198 2.198 0 0 0 1.267-1.984V3.06a2.2 2.2 0 0 0-2.199-2.198h-.04a2.2 2.2 0 0 0-2.198 2.198v.04c0 .884.528 1.643 1.284 1.987v2.836a5.395 5.395 0 0 0-2.616 1.241L7.073 4.381a2.19 2.19 0 0 0 .123-.715 2.187 2.187 0 1 0-2.187 2.187c.343 0 .666-.082.955-.222l6.451 4.683a5.388 5.388 0 0 0-.487 2.24c0 .822.183 1.601.512 2.299l-2.145 2.145a2.165 2.165 0 0 0-.87-.183 2.187 2.187 0 1 0 2.187 2.187c0-.313-.069-.61-.186-.879l2.13-2.13a5.42 5.42 0 1 0 4.608-8.163zm-.972 7.614a2.592 2.592 0 1 1 0-5.184 2.592 2.592 0 0 1 0 5.184z"/>
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-pb-gray-800 group-hover:text-orange-700">HubSpot</h3>
                <p className="text-sm text-pb-gray-600">Connect with Private App to sync Deals</p>
              </div>
              <ExternalLink className="w-5 h-5 text-pb-gray-400 group-hover:text-orange-600" />
            </div>
          </button>

          {/* Skip Option */}
          <button
            onClick={() => setCrmChoice('skip')}
            className="w-full p-6 border-2 border-pb-gray-200 rounded-xl hover:border-pb-gray-400 hover:bg-pb-gray-50 transition-all text-left group"
          >
            <div className="flex items-center">
              <div className="w-12 h-12 bg-pb-gray-100 rounded-xl flex items-center justify-center mr-4 group-hover:bg-pb-gray-200 transition-colors">
                <svg className="w-6 h-6 text-pb-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-pb-gray-800">Skip - ACE Only</h3>
                <p className="text-sm text-pb-gray-600">Use PartnerBuddy without CRM integration</p>
              </div>
            </div>
          </button>
        </div>
      </div>
    );
  }

  // Show specific CRM configuration based on choice
  if (crmChoice === 'salesforce') {
    return <SalesforceConfigStep tenant={tenant} onComplete={onComplete} onBack={() => setCrmChoice(null)} />;
  }

  if (crmChoice === 'hubspot') {
    return <HubSpotConfigStep tenant={tenant} onComplete={onComplete} onBack={() => setCrmChoice(null)} />;
  }

  if (crmChoice === 'skip') {
    return <SkipCRMStep tenant={tenant} onComplete={onComplete} onBack={() => setCrmChoice(null)} />;
  }

  return null;
}

function SalesforceConfigStep({ tenant, onComplete: _onComplete, onBack }: { tenant: TenantStatus; onComplete: () => void; onBack: () => void }) {
  void _onComplete; // OAuth flow redirects instead of calling onComplete
  const [instanceUrl, setInstanceUrl] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSaveAndAuthorize = async () => {
    if (!instanceUrl.trim()) {
      setError('Please enter your Salesforce instance URL');
      return;
    }
    if (!clientId.trim()) {
      setError('Please enter your Connected App Client ID');
      return;
    }
    if (!clientSecret.trim()) {
      setError('Please enter your Connected App Client Secret');
      return;
    }

    // Validate instance URL format (supports prod, dev, scratch, and lightning domains)
    const cleanUrl = instanceUrl.replace(/\/+$/, '');
    if (!cleanUrl.match(/^https:\/\/[\w.-]+\.(salesforce\.com|force\.com|lightning\.force\.com)$/)) {
      setError('Instance URL must be a valid Salesforce domain (e.g., https://yourorg.salesforce.com or https://yourorg.develop.lightning.force.com)');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Save credentials first
      await api.saveSalesforceCredentials(tenant.tenant_id, instanceUrl, clientId, clientSecret);
      // Redirect to OAuth flow
      window.location.href = api.getSalesforceOAuthUrl(tenant.tenant_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save Salesforce credentials');
      setSaving(false);
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-center mb-6">
        <button
          onClick={onBack}
          className="p-2 -ml-2 text-pb-gray-600 hover:text-pb-gray-800 hover:bg-pb-gray-100 rounded-lg transition-colors"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <h2 className="text-xl font-display font-bold text-pb-gray-800 ml-2">
          Connect Salesforce
        </h2>
      </div>

      <div className="bg-blue-50 rounded-xl p-6 mb-6">
        <h3 className="font-semibold text-blue-800 mb-2">Setup Instructions</h3>
        <ol className="list-decimal list-inside space-y-2 text-sm text-blue-700">
          <li>Go to Salesforce Setup → App Manager → New Connected App</li>
          <li>Enable OAuth Settings and add "api" and "refresh_token" scopes</li>
          <li>Set Callback URL to: <code className="bg-blue-100 px-1 rounded text-xs">https://api.partner.rhythmic.dev/register/salesforce/callback</code></li>
          <li>Copy your Client ID and Client Secret below</li>
        </ol>
      </div>

      <div className="space-y-4 mb-6">
        <div>
          <label className="block font-semibold text-pb-gray-800 mb-2">
            Salesforce Instance URL *
          </label>
          <input
            type="url"
            value={instanceUrl}
            onChange={(e) => { setInstanceUrl(e.target.value); setError(null); }}
            placeholder="https://yourorg.salesforce.com or https://yourorg.develop.lightning.force.com"
            className="w-full px-4 py-3 border-2 border-pb-gray-200 rounded-lg font-mono text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
          />
        </div>

        <div>
          <label className="block font-semibold text-pb-gray-800 mb-2">
            Connected App Client ID *
          </label>
          <input
            type="text"
            value={clientId}
            onChange={(e) => { setClientId(e.target.value); setError(null); }}
            placeholder="3MVG9..."
            className="w-full px-4 py-3 border-2 border-pb-gray-200 rounded-lg font-mono text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
          />
        </div>

        <div>
          <label className="block font-semibold text-pb-gray-800 mb-2">
            Connected App Client Secret *
          </label>
          <input
            type="password"
            value={clientSecret}
            onChange={(e) => { setClientSecret(e.target.value); setError(null); }}
            placeholder="Your client secret"
            className="w-full px-4 py-3 border-2 border-pb-gray-200 rounded-lg font-mono text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
          />
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-lg">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
            <p className="text-sm">{error}</p>
          </div>
        </div>
      )}

      <button
        onClick={handleSaveAndAuthorize}
        disabled={!instanceUrl.trim() || !clientId.trim() || !clientSecret.trim() || saving}
        className="w-full px-6 py-4 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {saving ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Connecting...
          </>
        ) : (
          <>
            <ExternalLink className="w-5 h-5 mr-2" />
            Authorize Salesforce
          </>
        )}
      </button>
    </div>
  );
}

type HubSpotAuthMethod = 'oauth' | 'private-app' | null;

function HubSpotConfigStep({ tenant, onComplete, onBack }: { tenant: TenantStatus; onComplete: () => void; onBack: () => void }) {
  const [authMethod, setAuthMethod] = useState<HubSpotAuthMethod>(null);

  // If user selected an auth method, show that config screen
  if (authMethod === 'oauth') {
    return <HubSpotOAuthConfig tenant={tenant} onComplete={onComplete} onBack={() => setAuthMethod(null)} />;
  }
  if (authMethod === 'private-app') {
    return <HubSpotPrivateAppConfig tenant={tenant} onComplete={onComplete} onBack={() => setAuthMethod(null)} />;
  }

  // Show auth method selection
  return (
    <div className="p-8">
      <div className="flex items-center mb-6">
        <button
          onClick={onBack}
          className="p-2 -ml-2 text-pb-gray-600 hover:text-pb-gray-800 hover:bg-pb-gray-100 rounded-lg transition-colors"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <h2 className="text-xl font-display font-bold text-pb-gray-800 ml-2">
          Connect HubSpot
        </h2>
      </div>

      <p className="text-pb-gray-600 mb-6">
        Choose how you'd like to connect your HubSpot account:
      </p>

      <div className="space-y-3">
        {/* OAuth Option - Recommended */}
        <button
          onClick={() => setAuthMethod('oauth')}
          className="w-full p-4 bg-white border-2 border-pb-gray-200 rounded-xl hover:border-orange-400 hover:bg-orange-50/50 transition-all text-left group"
        >
          <div className="flex items-start">
            <div className="w-5 h-5 rounded-full border-2 border-pb-gray-300 group-hover:border-orange-500 mt-0.5 flex-shrink-0" />
            <div className="ml-3 flex-1">
              <div className="flex items-center">
                <span className="font-semibold text-pb-gray-800">OAuth App</span>
                <span className="ml-2 text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full">Recommended</span>
              </div>
              <p className="text-sm text-pb-gray-500 mt-1">
                More secure - uses OAuth 2.0 flow with automatic token refresh
              </p>
            </div>
          </div>
        </button>

        {/* Private App Option */}
        <button
          onClick={() => setAuthMethod('private-app')}
          className="w-full p-4 bg-white border-2 border-pb-gray-200 rounded-xl hover:border-orange-400 hover:bg-orange-50/50 transition-all text-left group"
        >
          <div className="flex items-start">
            <div className="w-5 h-5 rounded-full border-2 border-pb-gray-300 group-hover:border-orange-500 mt-0.5 flex-shrink-0" />
            <div className="ml-3 flex-1">
              <span className="font-semibold text-pb-gray-800">Private App Token</span>
              <p className="text-sm text-pb-gray-500 mt-1">
                Simpler setup - enter your Private App access token directly
              </p>
            </div>
          </div>
        </button>
      </div>
    </div>
  );
}

function HubSpotOAuthConfig({ tenant, onComplete: _onComplete, onBack }: { tenant: TenantStatus; onComplete: () => void; onBack: () => void }) {
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSaveAndAuthorize = async () => {
    if (!clientId.trim()) {
      setError('Please enter the Client ID');
      return;
    }
    if (!clientSecret.trim()) {
      setError('Please enter the Client Secret');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Save OAuth credentials first
      await api.saveHubSpotOAuthCredentials(tenant.tenant_id, clientId, clientSecret);

      // Redirect to HubSpot OAuth
      window.location.href = api.getHubSpotOAuthUrl(tenant.tenant_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save credentials');
      setSaving(false);
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-center mb-6">
        <button
          onClick={onBack}
          className="p-2 -ml-2 text-pb-gray-600 hover:text-pb-gray-800 hover:bg-pb-gray-100 rounded-lg transition-colors"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <h2 className="text-xl font-display font-bold text-pb-gray-800 ml-2">
          Connect HubSpot (OAuth)
        </h2>
      </div>

      <div className="bg-orange-50 rounded-xl p-6 mb-6">
        <h3 className="font-semibold text-orange-800 mb-2">Setup Instructions</h3>
        <ol className="list-decimal list-inside space-y-2 text-sm text-orange-700">
          <li>Go to HubSpot Developer Portal → Apps</li>
          <li>Create a new App or use an existing one</li>
          <li>In Auth settings, add callback URL: <code className="bg-orange-100 px-1 rounded">https://api.partner.rhythmic.dev/register/hubspot/callback</code></li>
          <li>Enable scopes: crm.objects.deals.read, crm.objects.contacts.read, crm.objects.companies.read</li>
          <li>Copy your Client ID and Client Secret below</li>
        </ol>
      </div>

      <div className="space-y-4 mb-6">
        <div>
          <label className="block font-semibold text-pb-gray-800 mb-2">
            Client ID *
          </label>
          <input
            type="text"
            value={clientId}
            onChange={(e) => { setClientId(e.target.value); setError(null); }}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            className="w-full px-4 py-3 border-2 border-pb-gray-200 rounded-lg font-mono text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition-all"
          />
        </div>

        <div>
          <label className="block font-semibold text-pb-gray-800 mb-2">
            Client Secret *
          </label>
          <input
            type="password"
            value={clientSecret}
            onChange={(e) => { setClientSecret(e.target.value); setError(null); }}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            className="w-full px-4 py-3 border-2 border-pb-gray-200 rounded-lg font-mono text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition-all"
          />
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-lg">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
            <p className="text-sm">{error}</p>
          </div>
        </div>
      )}

      <button
        onClick={handleSaveAndAuthorize}
        disabled={!clientId.trim() || !clientSecret.trim() || saving}
        className="w-full px-6 py-4 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {saving ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Connecting...
          </>
        ) : (
          'Authorize HubSpot'
        )}
      </button>
    </div>
  );
}

function HubSpotPrivateAppConfig({ tenant, onComplete, onBack }: { tenant: TenantStatus; onComplete: () => void; onBack: () => void }) {
  const [apiKey, setApiKey] = useState('');
  const [portalId, setPortalId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!apiKey.trim()) {
      setError('Please enter your HubSpot API key');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await api.saveHubSpotCredentials(tenant.tenant_id, apiKey, portalId || undefined);
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save HubSpot credentials');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-center mb-6">
        <button
          onClick={onBack}
          className="p-2 -ml-2 text-pb-gray-600 hover:text-pb-gray-800 hover:bg-pb-gray-100 rounded-lg transition-colors"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <h2 className="text-xl font-display font-bold text-pb-gray-800 ml-2">
          Connect HubSpot (Private App)
        </h2>
      </div>

      <div className="bg-orange-50 rounded-xl p-6 mb-6">
        <h3 className="font-semibold text-orange-800 mb-2">Setup Instructions</h3>
        <ol className="list-decimal list-inside space-y-2 text-sm text-orange-700">
          <li>Go to HubSpot Settings → Integrations → Private Apps</li>
          <li>Create a new Private App with scopes: crm.objects.deals.read, crm.objects.companies.read</li>
          <li>Copy the Access Token below</li>
        </ol>
      </div>

      <div className="space-y-4 mb-6">
        <div>
          <label className="block font-semibold text-pb-gray-800 mb-2">
            HubSpot Private App Access Token *
          </label>
          <input
            type="password"
            value={apiKey}
            onChange={(e) => { setApiKey(e.target.value); setError(null); }}
            placeholder="pat-na1-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            className="w-full px-4 py-3 border-2 border-pb-gray-200 rounded-lg font-mono text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition-all"
          />
        </div>

        <div>
          <label className="block font-semibold text-pb-gray-800 mb-2">
            HubSpot Portal ID (Optional)
          </label>
          <input
            type="text"
            value={portalId}
            onChange={(e) => setPortalId(e.target.value)}
            placeholder="12345678"
            className="w-full px-4 py-3 border-2 border-pb-gray-200 rounded-lg font-mono text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition-all"
          />
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-lg">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
            <p className="text-sm">{error}</p>
          </div>
        </div>
      )}

      <button
        onClick={handleSave}
        disabled={!apiKey.trim() || saving}
        className="w-full px-6 py-4 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {saving ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Saving...
          </>
        ) : (
          'Connect HubSpot'
        )}
      </button>
    </div>
  );
}

function SkipCRMStep({ tenant, onComplete, onBack }: { tenant: TenantStatus; onComplete: () => void; onBack: () => void }) {
  const [skipping, setSkipping] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSkip = async () => {
    setSkipping(true);
    setError(null);

    try {
      await api.skipCRM(tenant.tenant_id);
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to skip CRM setup');
      setSkipping(false);
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-center mb-6">
        <button
          onClick={onBack}
          className="p-2 -ml-2 text-pb-gray-600 hover:text-pb-gray-800 hover:bg-pb-gray-100 rounded-lg transition-colors"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <h2 className="text-xl font-display font-bold text-pb-gray-800 ml-2">
          Skip CRM Integration
        </h2>
      </div>

      <div className="bg-pb-gray-50 rounded-xl p-6 mb-6">
        <h3 className="font-semibold text-pb-gray-800 mb-3">Without CRM integration, PartnerBuddy will:</h3>
        <ul className="space-y-2 text-sm text-pb-gray-600">
          <li className="flex items-center">
            <CheckCircle className="w-4 h-4 text-pb-blue mr-2 flex-shrink-0" />
            Search and view your ACE opportunities
          </li>
          <li className="flex items-center">
            <CheckCircle className="w-4 h-4 text-pb-blue mr-2 flex-shrink-0" />
            Create and update ACE opportunities
          </li>
          <li className="flex items-center">
            <CheckCircle className="w-4 h-4 text-pb-blue mr-2 flex-shrink-0" />
            Help with opportunity validation
          </li>
          <li className="flex items-center text-pb-gray-400">
            <svg className="w-4 h-4 mr-2 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
            <span className="line-through">Sync opportunities from your CRM</span>
          </li>
        </ul>
      </div>

      <p className="text-sm text-pb-gray-600 mb-6">
        You can always connect a CRM later from your settings.
      </p>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-lg">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
            <p className="text-sm">{error}</p>
          </div>
        </div>
      )}

      <button
        onClick={handleSkip}
        disabled={skipping}
        className="w-full px-6 py-4 bg-pb-orange text-white rounded-xl font-semibold hover:bg-pb-orange-dark hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {skipping ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Saving...
          </>
        ) : (
          'Continue without CRM'
        )}
      </button>
    </div>
  );
}

function parseTeamsChannelUrl(url: string): string | null {
  try {
    // Match: https://teams.microsoft.com/l/channel/<encoded_channel_id>/<display_name>?...
    const match = url.match(/teams\.microsoft\.com\/l\/channel\/([^/]+)/);
    if (match) {
      return decodeURIComponent(match[1]);
    }
  } catch {
    // Ignore malformed URLs
  }
  return null;
}

function ChannelsStep({ tenant, onComplete }: { tenant: TenantStatus; onComplete: () => void }) {
  const [channels, setChannels] = useState<string>(tenant.allowed_channels?.join(', ') || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkInput, setLinkInput] = useState('');
  const [linkSuccess, setLinkSuccess] = useState<string | null>(null);
  const isTeams = !!tenant.teams_configured;

  const handleSave = async () => {
    const channelList = channels.split(',').map(c => c.trim()).filter(Boolean);

    if (channelList.length === 0) {
      setError('Please enter at least one channel ID');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await api.updateAllowedChannels(tenant.tenant_id, channelList);
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save channels');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </div>
        <h2 className="text-xl font-display font-bold text-pb-gray-800 mb-2">
          Configure Allowed Channels
        </h2>
        <p className="text-pb-gray-600">
          Specify which {isTeams ? 'Teams channels' : 'Slack channels'} PartnerBuddy can respond in
        </p>
      </div>

      <div className="mb-6">
        <div className="bg-pb-gray-50 rounded-xl p-6">
          <label className="block font-semibold text-pb-gray-800 mb-2">
            Allowed Channel IDs
          </label>
          <p className="text-sm text-pb-gray-600 mb-3">
            {isTeams ? (
              <>
                Enter Microsoft Teams channel IDs where PartnerBuddy should respond (comma-separated).
                You can find a channel ID by opening the channel in Teams, clicking the three-dot menu, and selecting "Get link to channel" &mdash; the channel ID is in the URL.
              </>
            ) : (
              <>
                Enter Slack channel IDs where PartnerBuddy should respond (comma-separated).
                You can find a channel's ID by right-clicking on it in Slack and selecting "View channel details".
              </>
            )}
          </p>
          <input
            type="text"
            value={channels}
            onChange={(e) => {
              setChannels(e.target.value);
              setError(null);
            }}
            placeholder={isTeams ? '19:abc123@thread.tacv2, 19:def456@thread.tacv2' : 'C01234567, C89012345'}
            className="w-full px-4 py-3 border-2 border-pb-gray-200 rounded-lg focus:border-pb-orange focus:ring-2 focus:ring-pb-orange/20 outline-none transition-all"
          />
          <p className="text-xs text-pb-gray-500 mt-2">
            For security, PartnerBuddy will only respond in explicitly allowed channels.
          </p>
        </div>
      </div>

      {isTeams && (
        <div className="mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <label className="block font-semibold text-pb-gray-800 mb-2">
              Paste a Teams channel link
            </label>
            <p className="text-sm text-pb-gray-600 mb-3">
              Paste a Teams channel URL and we'll extract the channel ID for you.
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                value={linkInput}
                onChange={(e) => {
                  setLinkInput(e.target.value);
                  setLinkSuccess(null);
                }}
                placeholder="https://teams.microsoft.com/l/channel/..."
                className="flex-1 px-4 py-3 border-2 border-blue-200 rounded-lg focus:border-pb-orange focus:ring-2 focus:ring-pb-orange/20 outline-none transition-all text-sm"
              />
              <button
                type="button"
                onClick={() => {
                  const channelId = parseTeamsChannelUrl(linkInput.trim());
                  if (channelId) {
                    const existing = channels.split(',').map(c => c.trim()).filter(Boolean);
                    if (!existing.includes(channelId)) {
                      const updated = existing.length > 0 ? [...existing, channelId].join(', ') : channelId;
                      setChannels(updated);
                    }
                    setLinkInput('');
                    setLinkSuccess(`Added: ${channelId}`);
                    setError(null);
                  } else {
                    setError('Could not extract a channel ID from that URL. Please paste a Teams channel link.');
                  }
                }}
                disabled={!linkInput.trim()}
                className="px-4 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap"
              >
                Add
              </button>
            </div>
            {linkSuccess && (
              <p className="text-xs text-blue-600 mt-2">{linkSuccess}</p>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      <button
        onClick={handleSave}
        disabled={saving || !channels.trim()}
        className="w-full px-6 py-4 bg-pb-orange text-white rounded-xl font-semibold hover:bg-pb-orange-dark hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {saving ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Saving...
          </>
        ) : (
          'Complete Setup'
        )}
      </button>
    </div>
  );
}

// Scheduled Reports Configuration Component (supports multiple reports)
function ScheduledReportsSection({ tenant }: { tenant: TenantStatus }) {
  const [reports, setReports] = useState<ScheduledReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingReport, setEditingReport] = useState<ScheduledReport | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state for editing/creating
  const [formName, setFormName] = useState('');
  const [formEnabled, setFormEnabled] = useState(true);
  const [formFrequency, setFormFrequency] = useState<'daily' | 'weekly' | 'monthly'>('weekly');
  const [formDayOfWeek, setFormDayOfWeek] = useState(0);
  const [formDayOfMonth, setFormDayOfMonth] = useState(1);
  const [formTimeUtc, setFormTimeUtc] = useState('14:00');
  const [formTimezone, setFormTimezone] = useState('America/New_York');
  const [formChannelId, setFormChannelId] = useState('');
  const [formReportType, setFormReportType] = useState('pipeline_summary');
  const [formCustomPrompt, setFormCustomPrompt] = useState('');

  const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const REPORT_TYPES = [
    { value: 'pipeline_summary', label: 'Pipeline Summary' },
    { value: 'stale_opportunities', label: 'Stale Opportunities' },
    { value: 'monthly_forecast', label: 'Monthly Forecast' },
    { value: 'weekly_activity', label: 'Weekly Activity' },
    { value: 'custom', label: 'Custom Report' },
  ];
  const TIMEZONES = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'UTC', label: 'UTC' },
  ];

  // Load reports on mount
  useEffect(() => {
    loadReports();
  }, [tenant.tenant_id]);

  const loadReports = async () => {
    try {
      setLoading(true);
      const data = await api.listScheduledReports(tenant.tenant_id);
      setReports(data);
    } catch (err) {
      console.error('Failed to load reports:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormName('');
    setFormEnabled(true);
    setFormFrequency('weekly');
    setFormDayOfWeek(0);
    setFormDayOfMonth(1);
    setFormTimeUtc('14:00');
    setFormTimezone('America/New_York');
    setFormChannelId('');
    setFormReportType('pipeline_summary');
    setFormCustomPrompt('');
  };

  const populateForm = (report: ScheduledReport) => {
    setFormName(report.name);
    setFormEnabled(report.enabled);
    setFormFrequency(report.frequency);
    setFormDayOfWeek(report.day_of_week ?? 0);
    setFormDayOfMonth(report.day_of_month ?? 1);
    setFormTimeUtc(report.time_utc);
    setFormTimezone(report.timezone);
    setFormChannelId(report.channel_id);
    setFormReportType(report.report_type);
    setFormCustomPrompt(report.custom_prompt ?? '');
  };

  const handleStartCreate = () => {
    resetForm();
    setEditingReport(null);
    setIsCreating(true);
    setError(null);
  };

  const handleStartEdit = (report: ScheduledReport) => {
    populateForm(report);
    setEditingReport(report);
    setIsCreating(false);
    setError(null);
  };

  const handleCancel = () => {
    setEditingReport(null);
    setIsCreating(false);
    setError(null);
    resetForm();
  };

  const handleDelete = async (reportId: string) => {
    if (!confirm('Are you sure you want to delete this scheduled report?')) {
      return;
    }
    try {
      await api.deleteScheduledReport(tenant.tenant_id, reportId);
      setReports(reports.filter(r => r.report_id !== reportId));
      setSuccess('Report deleted successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete report');
    }
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      setError('Please enter a name for this report');
      return;
    }
    if (!formChannelId.trim()) {
      setError('Please select a channel for report delivery');
      return;
    }
    if (formReportType === 'custom' && !formCustomPrompt.trim()) {
      setError('Please enter a custom prompt for your report');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    const reportData: Omit<ScheduledReport, 'report_id'> = {
      name: formName,
      enabled: formEnabled,
      frequency: formFrequency,
      day_of_week: formFrequency === 'weekly' ? formDayOfWeek : undefined,
      day_of_month: formFrequency === 'monthly' ? formDayOfMonth : undefined,
      time_utc: formTimeUtc,
      timezone: formTimezone,
      channel_id: formChannelId,
      report_type: formReportType,
      custom_prompt: formReportType === 'custom' ? formCustomPrompt : undefined,
    };

    try {
      if (editingReport?.report_id) {
        // Update existing report
        await api.updateScheduledReport(tenant.tenant_id, editingReport.report_id, reportData);
        setReports(reports.map(r =>
          r.report_id === editingReport.report_id
            ? { ...reportData, report_id: editingReport.report_id }
            : r
        ));
        setSuccess('Report updated successfully!');
      } else {
        // Create new report
        const reportId = await api.createScheduledReport(tenant.tenant_id, reportData);
        setReports([...reports, { ...reportData, report_id: reportId }]);
        setSuccess('Report created successfully!');
      }
      handleCancel();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save report');
    } finally {
      setSaving(false);
    }
  };

  const getScheduleSummary = (report: ScheduledReport) => {
    const freq = report.frequency.charAt(0).toUpperCase() + report.frequency.slice(1);
    const type = REPORT_TYPES.find(t => t.value === report.report_type)?.label ?? report.report_type;
    let schedule = freq;
    if (report.frequency === 'weekly' && report.day_of_week !== undefined) {
      schedule += ` on ${DAYS_OF_WEEK[report.day_of_week]}`;
    } else if (report.frequency === 'monthly' && report.day_of_month !== undefined) {
      schedule += ` on day ${report.day_of_month}`;
    }
    return `${type} - ${schedule}`;
  };

  const isFormOpen = isCreating || editingReport !== null;

  return (
    <div className="bg-white border border-pb-gray-200 rounded-xl p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <svg className="w-5 h-5 text-pb-orange mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          <h3 className="font-semibold text-pb-gray-800">Scheduled Reports</h3>
        </div>
        {!isFormOpen && (
          <button
            onClick={handleStartCreate}
            className="text-sm text-pb-orange hover:text-pb-orange-dark font-medium flex items-center"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Report
          </button>
        )}
      </div>

      {/* Success Message */}
      {success && (
        <div className="mb-4 p-3 bg-pb-blue/10 border border-pb-blue/20 rounded-lg">
          <div className="flex items-center">
            <CheckCircle className="w-4 h-4 text-pb-blue mr-2" />
            <p className="text-sm text-pb-blue-dark">{success}</p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-pb-orange animate-spin" />
        </div>
      )}

      {/* Reports List */}
      {!loading && !isFormOpen && (
        <div className="space-y-3">
          {reports.length === 0 ? (
            <p className="text-sm text-pb-gray-500 italic">
              No scheduled reports configured. Click "Add Report" to create one.
            </p>
          ) : (
            reports.map((report) => (
              <div
                key={report.report_id}
                className="flex items-center justify-between p-3 bg-pb-gray-50 rounded-lg"
              >
                <div className="flex-1">
                  <div className="flex items-center">
                    <span className="font-medium text-pb-gray-800">{report.name}</span>
                    {report.enabled ? (
                      <span className="ml-2 px-2 py-0.5 text-xs bg-pb-blue/10 text-pb-blue rounded-full">Active</span>
                    ) : (
                      <span className="ml-2 px-2 py-0.5 text-xs bg-pb-gray-200 text-pb-gray-600 rounded-full">Disabled</span>
                    )}
                  </div>
                  <p className="text-sm text-pb-gray-500">{getScheduleSummary(report)}</p>
                  <p className="text-xs text-pb-gray-400 mt-0.5">Channel: {report.channel_id}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleStartEdit(report)}
                    className="p-2 text-pb-gray-500 hover:text-pb-orange transition-colors"
                    title="Edit report"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => report.report_id && handleDelete(report.report_id)}
                    className="p-2 text-pb-gray-500 hover:text-red-500 transition-colors"
                    title="Delete report"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Report Form */}
      {isFormOpen && (
        <div className="space-y-4">
          <h4 className="font-medium text-pb-gray-800">
            {editingReport ? 'Edit Report' : 'New Scheduled Report'}
          </h4>

          {/* Report Name */}
          <div>
            <label className="block text-sm font-medium text-pb-gray-700 mb-1">Report Name</label>
            <input
              type="text"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="e.g., Weekly Pipeline Summary"
              className="w-full px-3 py-2 border border-pb-gray-200 rounded-lg focus:border-pb-orange focus:ring-1 focus:ring-pb-orange outline-none"
            />
          </div>

          {/* Enable Toggle */}
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-pb-gray-700">Enable this report</label>
            <button
              type="button"
              onClick={() => setFormEnabled(!formEnabled)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                formEnabled ? 'bg-pb-orange' : 'bg-pb-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  formEnabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {/* Frequency */}
          <div>
            <label className="block text-sm font-medium text-pb-gray-700 mb-1">Frequency</label>
            <Select
              value={formFrequency}
              onChange={(val) => setFormFrequency(val as 'daily' | 'weekly' | 'monthly')}
              options={[
                { value: 'daily', label: 'Daily' },
                { value: 'weekly', label: 'Weekly' },
                { value: 'monthly', label: 'Monthly' },
              ]}
            />
          </div>

          {/* Day Selection */}
          {formFrequency === 'weekly' && (
            <div>
              <label className="block text-sm font-medium text-pb-gray-700 mb-1">Day of Week</label>
              <Select
                value={formDayOfWeek}
                onChange={(val) => setFormDayOfWeek(val as number)}
                options={DAYS_OF_WEEK.map((day, idx) => ({ value: idx, label: day }))}
              />
            </div>
          )}

          {formFrequency === 'monthly' && (
            <div>
              <label className="block text-sm font-medium text-pb-gray-700 mb-1">Day of Month</label>
              <Select
                value={formDayOfMonth}
                onChange={(val) => setFormDayOfMonth(val as number)}
                options={Array.from({ length: 28 }, (_, i) => ({ value: i + 1, label: String(i + 1) }))}
              />
            </div>
          )}

          {/* Time and Timezone */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-pb-gray-700 mb-1">Time</label>
              <Select
                value={formTimeUtc}
                onChange={(val) => setFormTimeUtc(val as string)}
                options={Array.from({ length: 24 }, (_, i) => {
                  const hour = i.toString().padStart(2, '0');
                  const label = i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`;
                  return { value: `${hour}:00`, label };
                })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-pb-gray-700 mb-1">Timezone</label>
              <Select
                value={formTimezone}
                onChange={(val) => setFormTimezone(val as string)}
                options={TIMEZONES}
              />
            </div>
          </div>

          {/* Channel ID */}
          <div>
            <label className="block text-sm font-medium text-pb-gray-700 mb-1">Delivery Channel</label>
            <Select
              value={formChannelId}
              onChange={(val) => setFormChannelId(val as string)}
              placeholder="Select a channel..."
              options={tenant.allowed_channels?.map((ch) => ({ value: ch, label: ch })) || []}
            />
            <p className="text-xs text-pb-gray-500 mt-1">
              Reports will be sent to this channel. Only allowed channels are shown.
            </p>
          </div>

          {/* Report Type */}
          <div>
            <label className="block text-sm font-medium text-pb-gray-700 mb-1">Report Type</label>
            <Select
              value={formReportType}
              onChange={(val) => setFormReportType(val as string)}
              options={REPORT_TYPES}
            />
          </div>

          {/* Custom Prompt */}
          {formReportType === 'custom' && (
            <div>
              <label className="block text-sm font-medium text-pb-gray-700 mb-1">Custom Report Prompt</label>
              <textarea
                value={formCustomPrompt}
                onChange={(e) => setFormCustomPrompt(e.target.value)}
                placeholder="Describe what you want in your report. For example: 'List all opportunities closing this quarter, grouped by stage, with their estimated values and key contacts.'"
                rows={4}
                className="w-full px-3 py-2 border border-pb-gray-200 rounded-lg focus:border-pb-orange focus:ring-1 focus:ring-pb-orange outline-none resize-none"
              />
              <p className="text-xs text-pb-gray-500 mt-1">
                Your prompt will be sent to the AI to generate a customized report.
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start">
                <AlertCircle className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 px-4 py-2 bg-pb-orange text-white rounded-lg font-semibold hover:bg-pb-orange-dark hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : editingReport ? (
                'Update Report'
              ) : (
                'Create Report'
              )}
            </button>
            <button
              onClick={handleCancel}
              className="px-4 py-2 border border-pb-gray-300 text-pb-gray-700 rounded-lg font-semibold hover:bg-pb-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Legacy single report config - kept for backwards compatibility
function ReportConfigSection({ tenant }: { tenant: TenantStatus }) {
  return <ScheduledReportsSection tenant={tenant} />;
}

function CompleteStep({ tenant }: { tenant: TenantStatus }) {
  const isTeams = !!tenant.teams_configured;

  // Channel editing state
  const [channels, setChannels] = useState<string>(tenant.allowed_channels?.join(', ') || '');
  const [savingChannels, setSavingChannels] = useState(false);
  const [channelError, setChannelError] = useState<string | null>(null);
  const [channelSuccess, setChannelSuccess] = useState<string | null>(null);
  const [isEditingChannels, setIsEditingChannels] = useState(false);
  const [linkInput, setLinkInput] = useState('');
  const [linkSuccess, setLinkSuccess] = useState<string | null>(null);

  // CRM editing state
  const [apiKey, setApiKey] = useState('');
  const [portalId, setPortalId] = useState('');
  const [savingCRM, setSavingCRM] = useState(false);
  const [crmError, setCrmError] = useState<string | null>(null);
  const [crmSuccess, setCrmSuccess] = useState<string | null>(null);
  const [isEditingCRM, setIsEditingCRM] = useState(false);

  // Determine which CRM is configured
  const configuredCRM = tenant.crm_type || (tenant.hubspot_configured ? 'hubspot' : tenant.salesforce_configured ? 'salesforce' : null);
  const crmLabel = configuredCRM === 'hubspot' ? 'HubSpot' : configuredCRM === 'salesforce' ? 'Salesforce' : 'CRM';

  const handleSaveChannels = async () => {
    const channelList = channels.split(',').map(c => c.trim()).filter(Boolean);

    if (channelList.length === 0) {
      setChannelError('Please enter at least one channel ID');
      return;
    }

    setSavingChannels(true);
    setChannelError(null);
    setChannelSuccess(null);

    try {
      // Use activate=false to just update channels without changing status
      await api.updateAllowedChannels(tenant.tenant_id, channelList, false);
      setChannelSuccess('Channels updated successfully!');
      setIsEditingChannels(false);
      // Clear success message after a few seconds
      setTimeout(() => setChannelSuccess(null), 3000);
    } catch (err) {
      setChannelError(err instanceof Error ? err.message : 'Failed to save channels');
    } finally {
      setSavingChannels(false);
    }
  };

  const handleSaveCRM = async () => {
    if (!apiKey.trim()) {
      setCrmError(`Please enter your ${crmLabel} credentials`);
      return;
    }

    setSavingCRM(true);
    setCrmError(null);
    setCrmSuccess(null);

    try {
      if (configuredCRM === 'salesforce') {
        // For Salesforce, redirect to OAuth flow
        window.location.href = api.getSalesforceOAuthUrl(tenant.tenant_id);
        return;
      }
      // Default to HubSpot Private App flow
      await api.saveHubSpotCredentials(tenant.tenant_id, apiKey, portalId || undefined);
      setCrmSuccess(`${crmLabel} credentials updated successfully!`);
      setIsEditingCRM(false);
      setApiKey('');
      setPortalId('');
      setTimeout(() => setCrmSuccess(null), 3000);
    } catch (err) {
      setCrmError(err instanceof Error ? err.message : `Failed to save ${crmLabel} credentials`);
    } finally {
      setSavingCRM(false);
    }
  };

  return (
    <div className="p-8">
      <div className="text-center mb-8">
        <div className="w-20 h-20 bg-pb-blue/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-pb-blue" />
        </div>
        
        <h2 className="text-2xl font-display font-bold text-pb-orange mb-2">
          Setup Complete!
        </h2>
        <p className="text-pb-gray-600 mb-8">
          PartnerBuddy is now ready to use in your {isTeams ? 'Teams organization' : 'Slack workspace'}
        </p>
      </div>

      <div className="bg-pb-gray-50 rounded-xl p-6 mb-8 text-left">
        <h3 className="font-semibold text-pb-gray-800 mb-4">Try it out:</h3>
        <div className="space-y-4 text-sm">
          {isTeams ? (
            <>
              <div className="flex items-start">
                <span className="w-6 h-6 rounded-full bg-pb-orange text-white text-xs flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">1</span>
                <div className="text-pb-gray-700">
                  <p className="mb-2">In Microsoft Teams, open one of your allowed channels and make sure the PartnerBuddy app is added to the channel.</p>
                </div>
              </div>
              <div className="flex items-start">
                <span className="w-6 h-6 rounded-full bg-pb-orange text-white text-xs flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">2</span>
                <div className="text-pb-gray-700">
                  <p className="mb-2">Mention <code className="bg-white px-2 py-0.5 rounded border">@PartnerBuddy</code> followed by your question:</p>
                  <code className="bg-white px-3 py-1.5 rounded border block">@PartnerBuddy show my pipeline</code>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-start">
                <span className="w-6 h-6 rounded-full bg-pb-orange text-white text-xs flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">1</span>
                <div className="text-pb-gray-700">
                  <p className="mb-2">In Slack, invite PartnerBuddy to your allowed channels:</p>
                  <code className="bg-white px-3 py-1.5 rounded border block">/invite @PartnerBuddy</code>
                </div>
              </div>
              <div className="flex items-start">
                <span className="w-6 h-6 rounded-full bg-pb-orange text-white text-xs flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">2</span>
                <div className="text-pb-gray-700">
                  <p className="mb-2">Mention <code className="bg-white px-2 py-0.5 rounded border">@PartnerBuddy</code> followed by your question:</p>
                  <code className="bg-white px-3 py-1.5 rounded border block">@PartnerBuddy show my pipeline</code>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* CRM Integration Section */}
      <div className="bg-white border border-pb-gray-200 rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-pb-orange mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            <h3 className="font-semibold text-pb-gray-800">{configuredCRM ? `${crmLabel} Integration` : 'CRM Integration'}</h3>
          </div>
          {!isEditingCRM && (
            <button
              onClick={() => setIsEditingCRM(true)}
              className="text-sm text-pb-orange hover:text-pb-orange-dark font-medium"
            >
              {configuredCRM ? 'Update' : 'Configure'}
            </button>
          )}
        </div>

        {isEditingCRM ? (
          <div>
            {configuredCRM === 'salesforce' ? (
              <>
                <p className="text-sm text-pb-gray-600 mb-4">
                  To update your Salesforce connection, you'll be redirected to re-authorize.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      window.location.href = api.getSalesforceOAuthUrl(tenant.tenant_id);
                    }}
                    className="flex-1 px-4 py-2 bg-pb-orange text-white rounded-lg font-semibold hover:bg-pb-orange-dark hover:shadow-lg transition-all flex items-center justify-center"
                  >
                    Re-authorize Salesforce
                  </button>
                  <button
                    onClick={() => setIsEditingCRM(false)}
                    className="px-4 py-2 border border-pb-gray-300 text-pb-gray-700 rounded-lg font-semibold hover:bg-pb-gray-50 transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-pb-gray-600 mb-4">
                  {configuredCRM === 'hubspot'
                    ? 'Update your HubSpot credentials. Enter a new API key to replace the existing one.'
                    : 'Connect HubSpot to sync deals with ACE opportunities.'}
                </p>

                <div className="space-y-4 mb-4">
                  <div>
                    <label className="block font-medium text-pb-gray-700 mb-1 text-sm">
                      HubSpot Private App Access Token *
                    </label>
                    <input
                      type="password"
                      value={apiKey}
                      onChange={(e) => {
                        setApiKey(e.target.value);
                        setCrmError(null);
                      }}
                      placeholder="pat-na1-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      className="w-full px-4 py-3 border-2 border-pb-gray-200 rounded-lg font-mono text-sm focus:border-pb-orange focus:ring-2 focus:ring-pb-orange/20 outline-none transition-all"
                    />
                    <p className="text-xs text-pb-gray-500 mt-1">
                      Create in HubSpot Settings → Integrations → Private Apps
                    </p>
                  </div>

                  <div>
                    <label className="block font-medium text-pb-gray-700 mb-1 text-sm">
                      Portal ID (Optional)
                    </label>
                    <input
                      type="text"
                      value={portalId}
                      onChange={(e) => setPortalId(e.target.value)}
                      placeholder="12345678"
                      className="w-full px-4 py-3 border-2 border-pb-gray-200 rounded-lg font-mono text-sm focus:border-pb-orange focus:ring-2 focus:ring-pb-orange/20 outline-none transition-all"
                    />
                  </div>
                </div>

                {crmError && (
                  <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start">
                      <AlertCircle className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-700">{crmError}</p>
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={handleSaveCRM}
                    disabled={savingCRM || !apiKey.trim()}
                    className="flex-1 px-4 py-2 bg-pb-orange text-white rounded-lg font-semibold hover:bg-pb-orange-dark hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {savingCRM ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Credentials'
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setIsEditingCRM(false);
                      setApiKey('');
                      setPortalId('');
                      setCrmError(null);
                    }}
                    className="px-4 py-2 border border-pb-gray-300 text-pb-gray-700 rounded-lg font-semibold hover:bg-pb-gray-50 transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="flex items-center">
            {configuredCRM ? (
              <>
                <CheckCircle className="w-5 h-5 text-pb-blue mr-2" />
                <span className="text-pb-gray-700">{crmLabel} Connected</span>
              </>
            ) : (
              <>
                <AlertCircle className="w-5 h-5 text-pb-gray-400 mr-2" />
                <span className="text-pb-gray-500">Not configured</span>
              </>
            )}
          </div>
        )}

        {crmSuccess && (
          <div className="mt-3 p-3 bg-pb-blue/10 border border-pb-blue/20 rounded-lg">
            <div className="flex items-center">
              <CheckCircle className="w-4 h-4 text-pb-blue mr-2" />
              <p className="text-sm text-pb-blue-dark">{crmSuccess}</p>
            </div>
          </div>
        )}
      </div>

      {/* Channel Management Section */}
      <div className="bg-white border border-pb-gray-200 rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-pb-gray-800">Allowed Channels</h3>
          {!isEditingChannels && (
            <button
              onClick={() => setIsEditingChannels(true)}
              className="text-sm text-pb-orange hover:text-pb-orange-dark font-medium"
            >
              Edit
            </button>
          )}
        </div>
        
        {isEditingChannels ? (
          <div>
            <p className="text-sm text-pb-gray-600 mb-3">
              Enter {isTeams ? 'Microsoft Teams' : 'Slack'} channel IDs where PartnerBuddy should respond (comma-separated).
            </p>
            <input
              type="text"
              value={channels}
              onChange={(e) => {
                setChannels(e.target.value);
                setChannelError(null);
              }}
              placeholder={isTeams ? '19:abc123@thread.tacv2, 19:def456@thread.tacv2' : 'C01234567, C89012345'}
              className="w-full px-4 py-3 border-2 border-pb-gray-200 rounded-lg focus:border-pb-orange focus:ring-2 focus:ring-pb-orange/20 outline-none transition-all mb-3"
            />

            {isTeams && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3">
                <p className="text-xs text-pb-gray-600 mb-2 font-medium">Paste a Teams channel link to extract the ID:</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={linkInput}
                    onChange={(e) => {
                      setLinkInput(e.target.value);
                      setLinkSuccess(null);
                    }}
                    placeholder="https://teams.microsoft.com/l/channel/..."
                    className="flex-1 px-3 py-2 border border-blue-200 rounded-lg focus:border-pb-orange focus:ring-2 focus:ring-pb-orange/20 outline-none transition-all text-xs"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const channelId = parseTeamsChannelUrl(linkInput.trim());
                      if (channelId) {
                        const existing = channels.split(',').map(c => c.trim()).filter(Boolean);
                        if (!existing.includes(channelId)) {
                          const updated = existing.length > 0 ? [...existing, channelId].join(', ') : channelId;
                          setChannels(updated);
                        }
                        setLinkInput('');
                        setLinkSuccess(`Added: ${channelId}`);
                        setChannelError(null);
                      } else {
                        setChannelError('Could not extract a channel ID from that URL.');
                      }
                    }}
                    disabled={!linkInput.trim()}
                    className="px-3 py-2 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    Add
                  </button>
                </div>
                {linkSuccess && (
                  <p className="text-xs text-blue-600 mt-1">{linkSuccess}</p>
                )}
              </div>
            )}

            {channelError && (
              <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start">
                  <AlertCircle className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{channelError}</p>
                </div>
              </div>
            )}
            
            <div className="flex gap-3">
              <button
                onClick={handleSaveChannels}
                disabled={savingChannels || !channels.trim()}
                className="flex-1 px-4 py-2 bg-pb-orange text-white rounded-lg font-semibold hover:bg-pb-orange-dark hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {savingChannels ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Channels'
                )}
              </button>
              <button
                onClick={() => {
                  setIsEditingChannels(false);
                  setChannels(tenant.allowed_channels?.join(', ') || '');
                  setChannelError(null);
                }}
                className="px-4 py-2 border border-pb-gray-300 text-pb-gray-700 rounded-lg font-semibold hover:bg-pb-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div>
            {tenant.allowed_channels && tenant.allowed_channels.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {tenant.allowed_channels.map((ch) => (
                  <span
                    key={ch}
                    className="px-3 py-1 bg-pb-gray-100 text-pb-gray-700 rounded-full text-sm font-mono"
                  >
                    {ch}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-pb-gray-500 text-sm italic">
                No channels configured. Click "Edit" to add channels.
              </p>
            )}
          </div>
        )}
        
        {channelSuccess && (
          <div className="mt-3 p-3 bg-pb-blue/10 border border-pb-blue/20 rounded-lg">
            <div className="flex items-center">
              <CheckCircle className="w-4 h-4 text-pb-blue mr-2" />
              <p className="text-sm text-pb-blue-dark">{channelSuccess}</p>
            </div>
          </div>
        )}
      </div>

      {/* Scheduled Reports Section */}
      <ReportConfigSection tenant={tenant} />

      <div className="grid grid-cols-2 gap-4 text-sm">
        <div className="bg-white border border-pb-gray-200 rounded-xl p-4">
          <div className="text-pb-gray-500 mb-1">{isTeams ? 'Organization' : 'Workspace'}</div>
          <div className="font-semibold text-pb-gray-800">{(isTeams ? tenant.teams_tenant_name : tenant.workspace_name) || 'Connected'}</div>
        </div>
        <div className="bg-white border border-pb-gray-200 rounded-xl p-4">
          <div className="text-pb-gray-500 mb-1">Status</div>
          <div className="font-semibold text-pb-blue">Active</div>
        </div>
      </div>
    </div>
  );
}
